import React from 'react';
import { newRelicNoticeError } from '@de/utils/newRelicActions';

export interface LeadFlowDrawerBoundaryProps {
  readonly children: React.ReactNode;
  /**
   * Invoked from `componentDidCatch` after a drawer-body render throw is
   * contained. The provider passes its silent slideout-reset callback so a
   * caught error fully degrades the drawer (resets the open state) rather
   * than leaving empty chrome mounted — without recording the contained
   * crash as a user-initiated close.
   */
  readonly onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface LeadFlowDrawerBoundaryState {
  readonly hasError: boolean;
}

/**
 * Narrow error boundary around the governed lead-capture drawer body.
 *
 * SAD §14 reliability invariant: "no single failure crashes the entire
 * experience." The drawer body (`SingleProgramSubmission` and its
 * PII / TCPA / value-prop children) renders on the render *after*
 * `openLeadFlow` sets state — outside the action dispatcher's try/catch —
 * so a render throw there (an edge-case coerced `/match` result, a
 * malformed by-id `offeringDetail` projection, or a child dereference)
 * would otherwise escape to the host `DynamicExperienceErrorBoundary` and
 * blank the whole results experience.
 *
 * This boundary contains that throw to the drawer alone: it renders
 * nothing (the drawer collapses) and notifies `onError` so the provider
 * can reset the slideout. It reports the failure to browser telemetry
 * (New Relic) so a contained-but-systemic drawer break stays detectable in
 * monitoring, and never rethrows — keeping the surrounding
 * `DynamicExperience` mounted.
 */
export class LeadFlowDrawerBoundary extends React.Component<
  LeadFlowDrawerBoundaryProps,
  LeadFlowDrawerBoundaryState
> {
  state: LeadFlowDrawerBoundaryState = { hasError: false };

  static getDerivedStateFromError(): LeadFlowDrawerBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Route the contained failure through the shared New Relic seam rather
    // than the console alone — `console.error` is not collected, so a
    // systemic drawer break would otherwise be invisible once this boundary
    // stops it from reaching the host error boundary. The seam owns the SSR
    // guard and the absent-agent fallback, so reporting is best-effort and
    // never itself throws.
    newRelicNoticeError(error, {
      source: 'LeadFlowDrawerBoundary',
      componentStack: info.componentStack ?? '',
    });
    this.props.onError?.(error, info);
  }

  render(): React.ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
